import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { ExternalContact, ExternalContactInsert, ExternalContactUpdate } from '../types'

const EXTERNAL_CONTACTS_QUERY_KEY = ['external_contacts']

/**
 * Fetch external contacts for a residence
 */
export function useExternalContacts(residenceId: string | undefined) {
  return useQuery({
    queryKey: [...EXTERNAL_CONTACTS_QUERY_KEY, residenceId],
    queryFn: async () => {
      if (!residenceId) throw new Error('Residence ID is required')

      const { data, error } = await supabase
        .from('external_contacts')
        .select('*')
        .eq('residence_id', residenceId)
        .order('last_name', { ascending: true })

      if (error) throw error
      return data as ExternalContact[]
    },
    enabled: !!residenceId,
  })
}

/**
 * Fetch a single external contact by ID
 */
export function useExternalContact(id: string | undefined) {
  return useQuery({
    queryKey: [...EXTERNAL_CONTACTS_QUERY_KEY, 'detail', id],
    queryFn: async () => {
      if (!id) throw new Error('Contact ID is required')

      const { data, error } = await supabase
        .from('external_contacts')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data as ExternalContact
    },
    enabled: !!id,
  })
}

/**
 * Create a new external contact
 */
export function useCreateExternalContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (contact: ExternalContactInsert) => {
      const { data, error } = await supabase
        .from('external_contacts')
        .insert(contact)
        .select()
        .single()

      if (error) throw error
      return data as ExternalContact
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: EXTERNAL_CONTACTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...EXTERNAL_CONTACTS_QUERY_KEY, data.residence_id] })
    },
  })
}

/**
 * Update an existing external contact
 */
export function useUpdateExternalContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: ExternalContactUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('external_contacts')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as ExternalContact
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: EXTERNAL_CONTACTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...EXTERNAL_CONTACTS_QUERY_KEY, 'detail', data.id] })
    },
  })
}

/**
 * Delete an external contact
 */
export function useDeleteExternalContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('external_contacts')
        .delete()
        .eq('id', id)

      if (error) throw error
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXTERNAL_CONTACTS_QUERY_KEY })
    },
  })
}
